import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { isTop50, isTop100 } from "@/data/topPages";
import { getFreshIntro } from "@/data/freshIntros";
import { Top50Interlinks } from "./Top50Interlinks";
import { RelatedTilesPair } from "./RelatedTilesPair";
import { PlatformSelectionDisclaimer } from "./PlatformSelectionDisclaimer";

/**
 * Injects (via portals) the following enhancements on the current route:
 *  - Fresh 2026 subheadline + ~300-word intro after the first <h1> (top-50 only)
 *  - Review + article tiles, top-50 interlinks (top-50 only, at end of <main>)
 *  - EEAT platform-selection disclaimer (top-100, before Footer within <main>)
 *
 * This lets us update 100+ pages without editing each page component.
 * H1 tags are never modified — we only insert siblings.
 */
export const GlobalPageEnhancements = () => {
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, "") || "/";
  const [introHost, setIntroHost] = useState<HTMLElement | null>(null);
  const [bottomHost, setBottomHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setIntroHost(null);
    setBottomHost(null);

    const attach = () => {
      // ---- Intro host: after first <h1> ----
      const introMarkerId = "global-fresh-intro-slot";
      let intro = document.getElementById(introMarkerId);
      if (!intro) {
        const h1 = document.querySelector("main h1") || document.querySelector("h1");
        if (h1?.parentElement) {
          intro = document.createElement("div");
          intro.id = introMarkerId;
          intro.setAttribute("data-global-enhancement", "intro");
          // Match the heading's color so the subheadline stays readable on colored heroes.
          intro.style.color = window.getComputedStyle(h1).color;
          h1.parentElement.insertBefore(intro, h1.nextSibling);
        }
      }
      if (intro) {
        setIntroHost(intro);
        // Avoid a double subheadline: hide the page's own hero paragraph when we
        // inject a fresh 2026 subheadline right above it.
        if (isTop50(path) && getFreshIntro(path)) {
          const next = intro.nextElementSibling as HTMLElement | null;
          if (next && next.tagName === "P") {
            next.setAttribute("data-global-hidden", "true");
            next.style.display = "none";
          }
        }
      }


      // ---- Bottom host: end of <main>, before Footer ----
      const bottomMarkerId = "global-page-bottom-slot";
      let bottom = document.getElementById(bottomMarkerId);
      if (!bottom) {
        const main = document.querySelector("main");
        if (main) {
          bottom = document.createElement("div");
          bottom.id = bottomMarkerId;
          bottom.setAttribute("data-global-enhancement", "bottom");
          bottom.className = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
          main.appendChild(bottom);
        }
      }
      if (bottom) setBottomHost(bottom);
    };

    // Retry a few times because pages are lazy-loaded and mount after route change.
    const timers: number[] = [];
    [0, 100, 300, 800, 1500].forEach((delay) => {
      timers.push(window.setTimeout(attach, delay));
    });

    return () => {
      timers.forEach(clearTimeout);
      // Clean up injected hosts so they don't leak between routes.
      document.querySelectorAll<HTMLElement>('[data-global-hidden="true"]').forEach((el) => {
        el.style.display = "";
        el.removeAttribute("data-global-hidden");
      });
      document.getElementById("global-fresh-intro-slot")?.remove();
      document.getElementById("global-page-bottom-slot")?.remove();
    };
  }, [path]);

  const fresh = getFreshIntro(path);
  const showTop50 = isTop50(path);
  const showTop100 = isTop100(path);

  return (
    <>
      {introHost && showTop50 && fresh &&
        createPortal(
          <div className="mt-4 max-w-3xl mx-auto text-center">
            <p className="text-lg sm:text-xl opacity-90 leading-relaxed">{fresh.subheadline}</p>
            {fresh.updated && (
              <p className="mt-2 text-sm opacity-70">
                Aktualisiert am{" "}
                <time dateTime={fresh.updated}>
                  {new Date(fresh.updated).toLocaleDateString("de-DE", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </time>
              </p>
            )}
          </div>,
          introHost,
        )}

      {bottomHost &&
        createPortal(
          <>
            {showTop50 && (
              <div className="my-8">
                <RelatedTilesPair currentPath={path} />
              </div>
            )}
            {showTop50 && <Top50Interlinks currentPath={path} />}
            {showTop100 && <PlatformSelectionDisclaimer />}
          </>,
          bottomHost,
        )}
    </>
  );
};
